// src/components/product/ProductAddonSection.tsx
import * as React from "react";
import { Typography, Button, Table, Input, InputNumber, Switch } from "antd";
import { PlusOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { AddonMapping } from "src/types/product";

type Props = {
  value: AddonMapping[];
  onChange: (value: AddonMapping[]) => void;
};

type AddonInputRow = {
  id: string;
  groupName: string;
  valueNames: string; // 콤마로 구분된 값
  prices: string; // 콤마로 구분된 가격
};

export default function ProductAddonSection({ value, onChange }: Props) {
  const [inputRows, setInputRows] = React.useState<AddonInputRow[]>([
    { id: "1", groupName: "", valueNames: "", prices: "" },
  ]);
  const inputColumnStyle: React.CSSProperties = { minWidth: 180, flex: 1 };
  const inputLabelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: 4,
  };

  const addInputRow = () => {
    setInputRows([
      ...inputRows,
      { id: Date.now().toString(), groupName: "", valueNames: "", prices: "" },
    ]);
  };

  const removeInputRow = (id: string) => {
    setInputRows(inputRows.filter((r) => r.id !== id));
  };

  const updateInputRow = (id: string, field: keyof AddonInputRow, newValue: string) => {
    setInputRows(inputRows.map((r) => (r.id === id ? { ...r, [field]: newValue } : r)));
  };

  const handleGenerate = () => {
    const generated: AddonMapping[] = [];
    let displayOrder = 0;

    inputRows.forEach((row) => {
      if (!row.groupName.trim() || !row.valueNames.trim()) return;

      const valueNameList = row.valueNames
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      const priceList = row.prices
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);

      valueNameList.forEach((valueName, idx) => {
        const price = priceList[idx] ? Number(priceList[idx]) : 0;
        generated.push({
          mappingName: `${row.groupName}-${valueName}`,
          addonPrice: price,
          stockQuantity: 0,
          isSoldout: false,
          isActive: true,
          displayOrder: displayOrder++,
          addonValues: [
            {
              addonGroupName: row.groupName,
              addonValueName: valueName,
              displayOrder: 0,
            },
          ],
        });
      });
    });

    onChange(generated);
  };

  const valueRef = React.useRef(value);
  React.useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const updateAddonMapping = <K extends keyof AddonMapping>(
    index: number,
    field: K,
    newValue: AddonMapping[K]
  ) => {
    const updated = [...valueRef.current];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange(updated);
  };

  const removeAddonMapping = (index: number) => {
    onChange(valueRef.current.filter((_, i) => i !== index));
  };

  const updateAddonMappingRef = React.useRef(updateAddonMapping);
  const removeAddonMappingRef = React.useRef(removeAddonMapping);

  React.useEffect(() => {
    updateAddonMappingRef.current = updateAddonMapping;
    removeAddonMappingRef.current = removeAddonMapping;
  });

  const columns: ColumnsType<AddonMapping> = React.useMemo(() => [
    {
      title: "No.",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "추가상품명",
      dataIndex: "mappingName",
      width: 180,
      render: (text: string) => <Typography.Text>{text}</Typography.Text>,
    },
    {
      title: "추가상품값",
      width: 150,
      render: (_, record) => {
        const valueNames = record.addonValues.map((v) => v.addonValueName).join(", ");
        return <Typography.Text>{valueNames}</Typography.Text>;
      },
    },
    {
      title: "추가상품가",
      dataIndex: "addonPrice",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.addonPrice}
          onChange={(val) => updateAddonMappingRef.current(index, "addonPrice", val || 0)}
          min={0}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "재고수량",
      dataIndex: "stockQuantity",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.stockQuantity}
          onChange={(val) => updateAddonMappingRef.current(index, "stockQuantity", val || 0)}
          min={0}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "판매상태",
      dataIndex: "isSoldout",
      width: 100,
      render: (_, record) => (
        <Typography.Text>{record.isSoldout ? "품절" : "판매중"}</Typography.Text>
      ),
    },
    {
      title: "관리코드",
      width: 120,
      render: () => <Typography.Text>-</Typography.Text>,
    },
    {
      title: "사용여부",
      dataIndex: "isActive",
      width: 100,
      align: "center",
      render: (_, record, index) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => updateAddonMappingRef.current(index, "isActive", checked)}
        />
      ),
    },
    {
      title: "삭제",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, __, index) => (
        <Button type="text" icon={<DeleteOutlined />} onClick={() => removeAddonMappingRef.current(index)} />
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <div>
      <Typography.Title level={5}>추가상품 입력</Typography.Title>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        추가상품값과 가격을 콤마(,)로 구분하여 입력하세요. 예: "옵션1,옵션2"
      </Typography.Text>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {inputRows.map((row, index) => (
          <div key={row.id} style={{ display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}>
            <div style={inputColumnStyle}>
              {index === 0 && (
                <Typography.Text type="secondary" style={inputLabelStyle}>
                  추가상품명
                </Typography.Text>
              )}
              <Input
                value={row.groupName}
                onChange={(e) => updateInputRow(row.id, "groupName", e.target.value)}
                placeholder="ex) 허니콤츄S"
              />
            </div>
            <div style={inputColumnStyle}>
              {index === 0 && (
                <Typography.Text type="secondary" style={inputLabelStyle}>
                  추가상품값
                </Typography.Text>
              )}
              <Input
                value={row.valueNames}
                onChange={(e) => updateInputRow(row.id, "valueNames", e.target.value)}
                placeholder="베지,칠면조"
              />
            </div>
            <div style={inputColumnStyle}>
              {index === 0 && (
                <Typography.Text type="secondary" style={inputLabelStyle}>
                  추가상품가
                </Typography.Text>
              )}
              <Input
                value={row.prices}
                onChange={(e) => updateInputRow(row.id, "prices", e.target.value)}
                placeholder="0,0"
              />
            </div>
            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              {inputRows.length > 1 && (
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => removeInputRow(row.id)}
                />
              )}
              {index === inputRows.length - 1 && (
                <>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addInputRow}
                  />
                  <Button
                    onClick={handleGenerate}
                    style={{ backgroundColor: "#52c41a", color: "white", border: "none" }}
                  >
                    추가상품 적용
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
        추가상품 목록 (총 {value.length}개)
      </Typography.Text>

      {value.length > 0 ? (
        <Table
          columns={columns}
          dataSource={value}
          rowKey={(_, index) => `addon-${index}`}
          pagination={false}
          bordered
          size="small"
          scroll={{ x: 1000 }}
        />
      ) : (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            padding: 40,
            textAlign: "center",
            color: "#999",
          }}
        >
          추가상품을 입력하고 "옵션으로 적용" 버튼을 눌러주세요.
        </div>
      )}
    </div>
  );
}
